import { html, nothing, PropertyValueMap, TemplateResult, CSSResultGroup } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { DirectiveResult } from 'lit/directive';
import { ClassMapDirective } from 'lit/directives/class-map';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { nanoid } from 'nanoid';
import { FormController } from '../../controllers/form';
import { SlotController } from '../../controllers/slot-controller';
import type { BlocksChangeEvent } from '../../events/blocks-change';
import type { CommonAriaLiveValue } from '../../util/common-modal';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../field-note/field-note';
import '../icon/icons/warning-diamond-filled';
import '../icon/icons/warning-triangle-filled';
import '../skeleton/skeleton';
import type { AutocompleteAttribute, InputSize, InputValidationType, TextInputType } from './input-field.model';
import styles from './input-field.styles';

export type Value = {
  (value: number | bigint): string;
};

/**
 * @slot after - The component after content
 * @slot char-insert-before - The before content
 * @slot char-insert-after - The after content
 * @slot word-count - Displays the no.of words
 * @slot custom-count - Can displays custom count
 * @slot field-note - slot for custom field notes
 * @slot warning - slot for custom warning notes
 * @slot error - slot for custom error notes
 * @fires bl-change - emitted when value is changed
 * @csspart base - The component's base wrapper.
 * @csspart label-skeleton - The component's label skeleton.
 * @csspart body-loader - The component's body loader.
 * @csspart body-skeleton - The component's body skeleton.
 * @csspart field-note-loader - The component's field-note-loader.
 * @csspart field-note-skeleton - The component's field-note-skeleton.
 * @csspart wrapper - The component's wrapper.
 * @csspart body - The component's body wrapper.
 * @csspart input-field - The component's `input` element.
 * @csspart label-wrapper - The component's label wrapper.
 * @csspart label-container - The component's label container.
 * @csspart label-inner-wrapper - The component's label inner-wrapper.
 * @csspart part-label - The component's label.
 * @csspart part-notation - The component's notation element.
 * @csspart part-word-count - The component's word-count slot.
 * @csspart part-custom-count - The component's custom-count slot.
 * @csspart max-length - The component's max-length value.
 * @csspart status-icon - The component's status-icon.
 * @csspart part-after - The component's after slot.
 * @csspart before - The component's before slot.
 * @csspart part-char-insert-before - The component's char-insert-before slot.
 * @csspart part-char-insert-after - The component's char-insert-after slot.
 * @csspart footer - The component's footer wrapper.
 * @csspart field-notes - The component's field-notes.
 * @csspart field-note-wrapper - The component's field-note wrapper.
 * @csspart part-field-note - The component's field-note slot.
 * @csspart part-warning - The component's warning slot.
 * @csspart part-error - The component's error slot.
 */

export class BlocksInputField extends BlocksElement {
  static formAssociated = true;static styles: CSSResultGroup = styles;

  protected formController = new FormController(this);

  /**
   * The unique id of the field
   */
  @property({ type: String })
  fieldId: string | undefined;

  /**
   * The form field's label
   */
  @property({ type: String })
  label = 'Label';

  /**
   * Placeholder attribute
   */
  @property({ type: String })
  placeholder = '';

  /**
   * Checkbox name attribute property
   */
  @property({ type: String })
  name: string | undefined;

  /**
   * Value
   */
  @property({ type: String, reflect: true })
  value = '';

  /**
   * Aria describedby
   * 1) Used to connect the field note in select field to the select menu for accessibility
   */
  @property({ type: String })
  ariaDescribedBy: string | undefined;

  /**
   * Aria label attribute
   * - Provides an accessible name for the input
   */
  @property({ type: String })
  arialabel: string | undefined;

  /**
   * Aria Unit label attribute
   * - Provides an accessible unit label for the input
   */
  @property({ type: String })
  ariaUnitLabel: string | undefined;

  /**
   * Required attribute
   */
  @property({ type: Boolean })
  required = false;

  /**
   * Notation is used for setting required or optional label
   */
  @property({ type: String })
  notation: string | undefined;

  /**
   * Disabled attribute
   */
  @property({ type: Boolean, reflect: true })
  disabled: boolean | undefined;

  /**
   * AutoFocus attribute
   */
  @property({ type: Boolean, reflect: true })
  autoFocus: boolean | undefined;

  /**
   * Readonly attribute
   */
  @property({ type: Boolean, reflect: true })
  readonly: boolean | undefined;

  /**
   * Type variants
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**text** renders a standard text input</li>
   * <li>**email** renders a text input specifically for an email format</li>
   * <li>**number** renders an input for number values only</li>
   * <li>**password** renders an input for a password input format</li>
   * <li>**url** renders an input for urls only</li>
   * <li>**tel** renders an input for telephone number values only</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String, reflect: true })
  type: TextInputType = 'text';

  /**
   * Hide label?
   */
  @property({ type: Boolean })
  hideLabel: boolean | undefined;

  /**
   * Hide default stepper that comes for input-field which has type number
   */
  @property({ type: Boolean })
  hideStepper: boolean | undefined;

  /**
   * Fieldnote text
   */
  @property({ reflect: true })
  fieldNote: string | undefined;

  /**
   * Errornote text
   */
  @property({ type: String })
  errorNote: string | undefined;

  /**
   * WarningNote text
   */
  @property({ type: String })
  warningNote: string | undefined;

  /**
   * The maxlength defines the longest string length that the user can enter into the field
   */
  @property({ type: Number, reflect: true })
  maxlength: number | undefined;

  /**
   * Status of input field
   */
  @property({ type: String })
  status: InputValidationType;
  /**
   * Active state
   */
  @property({ type: Boolean, reflect: true })
  isActive: boolean | undefined;

  /**
   * Formatted number boolean
   */
  @property({ type: Boolean, reflect: true })
  formatted: boolean | undefined;

  /**
   * Loading state
   * 1) Adds a loader if is true
   */
  @property({ type: Boolean })
  isLoading: boolean | undefined;

  /**
   * Locale property
   */
  @property({ type: String })
  locale: string | undefined;

  /**
   * Currency property
   */
  @property({ type: String })
  currency: string | undefined;

  /**
   * Style sizes
   */
  @property({ type: String })
  size: InputSize = 'lg';

  /**
   * Autocomplete property
   */
  @property({ type: String })
  autocomplete: AutocompleteAttribute = 'on';

  /**pattern attribute defines a regular expression to validate against input.
   * It validates the input value against the defined pattern, but does not actually restrict the characters that can be entered into the input field
   */
  @property({ type: String, reflect: true }) pattern: string | undefined;

  /**
   * The minlength attribute specifies the minimum number of characters
   * that must be entered in an input field for it to be considered valid.
   */
  @property({ type: Number, reflect: true }) minlength: number | undefined;

  /**
   * The input's minimum value.
   */
  @property({ type: Number, reflect: true }) min: number | undefined;

  /**
   * The input's maximum value.
   */
  @property({ type: Number, reflect: true }) max: number | undefined;

  /**
   * The step attribute is a number that specifies the granularity that the value must adhere to or the keyword any
   */
  @property({ type: Number }) step: number | undefined;

  /**
   * A boolean property to specify whether the word count should be hidden or displayed.
   */
  @property({ type: Boolean }) hideWordCount = false;

  /**
   * When any wheel event is attached to the document, it is propagated to the native input element also in capturing phase.
   * And due to this when mouse scroll is done, both the page will scroll and input element value will also change.
   * This is due to special case handling of wheel events on the document object, described in https://chromestatus.com/feature/6662647093133312
   * Since, it is assumed that wheel handlers on the document are passive, we would need to add useCapture to the wheel events.
   * But, in scenarios where the events will be added automatically by the frameworks to either the document or body or root node, this props `disableWheelScroll` can be used.
   * While using this, `hideStepper` will also becomes true to avoid flickering of stepper buttons.
   */
  @property({ type: Boolean }) disableWheelScroll = false;

  /**
   * Defines a template for localizing character count messages for screen reader announcements.
   */
  @property({ type: String })
  charCountTemplate: string | undefined;

  /**
   * The ARIA live region politeness level for the fieldNote. To be used
   * to prioritize the announcement of helper Text
   *
   * @type {string}
   * @default "polite"
   */
  @property({ type: String })
  arialive: CommonAriaLiveValue = 'polite';

  /**
   * Name or Title for the warning icon
   * meant for accessibility purposes only
   */
  @property({ type: String })
  warningIconTitle = 'warning icon';

  /**
   * Name or Title for the error icon
   * meant for accessibility purposes only
   */
  @property({ type: String })
  errorIconTitle = 'error icon';

  @query('input') private inputEl!: HTMLInputElement;

  /**
   * state for aria-describedby of Input field
   */
  @state()
  private descriptionIds: string | undefined;

  /**
   * Variables for number conversion
   * 1) Currency regex var
   * 2) Group regex var
   * 3) Decimal regex var
   * 4) Numeral regex var
   * 5) Index function
   */
  @state()
  private _currency: RegExp | undefined; /*1*/

  @state()
  private _group: RegExp | undefined; /*2*/

  @state()
  private _decimal: RegExp | undefined; /*3*/

  @state()
  private _numeral: RegExp | undefined; /*4*/

  @state()
  private _index: ((d: string) => number | undefined) | undefined; /*5*/

  /**
   * To keep track of not typing state
   */
  @state() private isNotTyping = true;

  /**
   * state for aria-label of Input field
   */
  @state()
  private combinedAriaLabel: string | undefined;

  /**
   1) Id for CharacterCount at right end of input-field
   2) Id for the slot at right end of input-field
   3) Id for the Character slot at right end of input-field
   4) Id for the prefix Character slot at start of input-field
   */
  private wordCountId = nanoid(); /*1*/

  private slotAfterId = nanoid(); /*2*/

  private charAfterId = nanoid(); /*3*/

  private charBeforeId = nanoid(); /*4*/

  private internals_ = this.attachInternals?.();

  protected slots = new SlotController(this, 'after', 'char-insert-before', 'char-insert-after', 'word-count','custom-count', 'field-note', 'error', 'warning');

  private get hasCharInsertBeforeSlot(): boolean {
    return this.slots.hasSlotted('char-insert-before');
  }
  private get hasCharInsertAfterSlot(): boolean {
    return this.slots.hasSlotted('char-insert-after');
  }
  private get hasAfterSlot(): boolean {
    return this.slots.hasSlotted('after');
  }
  private get hasFieldNoteSlot(): boolean {
    return this.slots.hasSlotted('field-note');
  }
  private get hasWarningSlot(): boolean {
    return this.slots.hasSlotted('warning');
  }
  private get hasErrorSlot(): boolean {
    return this.slots.hasSlotted('error');
  }

  public get inputElement() {
    return this.inputEl;
  }

  /**
   * Associates the control with a form element
   */
  get form(): HTMLFormElement | null {
    return this.internals_?.form;
  }

  protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    this.currency = this.currency ? this.currency : 'USD';
    this.locale = this.locale ? this.locale : 'en-US';

    const format: Intl.NumberFormat = new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: this.currency
    });
    const formatNum: Intl.NumberFormat = new Intl.NumberFormat(this.locale);
    const parts: Intl.NumberFormatPart[] = format.formatToParts(12345.6);
    const numerals = Array.from({ length: 10 }).map((_, i) => formatNum.format(i));
    const index = new Map(numerals.map((d, i) => [d, i]));
    this._currency = new RegExp(`[${parts.find((d: Intl.NumberFormatPart) => d.type === 'currency')?.value}]`);
    this._group = new RegExp(`[${parts.find((d: Intl.NumberFormatPart) => d.type === 'group')?.value}]`, 'g');
    this._decimal = new RegExp(`[${parts.find((d: Intl.NumberFormatPart) => d.type === 'decimal')?.value}]`);
    this._numeral = new RegExp(`[${numerals.join('')}]`, 'g');
    this._index = (d: string) => index.get(d);
  }

  connectedCallback() {
    super.connectedCallback();
    this.fieldId = this.fieldId || nanoid();
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('value')) {
      this.internals_?.setFormValue(`${this.value}`);
    }
    if (_changedProperties.has('autoFocus') && this.autoFocus) {
      this.focus();
    }
  }

  override focus(): void {
    this.inputEl?.focus();
  }

  /**
   * Change output binding
   * @param $event focused element
   */
  private onChanged = (e: Event) => {
    let value = (e.target as HTMLInputElement).value;
    if (this.type === 'number') {
      if (this.maxlength && value.length > this.maxlength) {
        value = value.slice(0, this.maxlength);
        this.inputEl.value = value;
        if (value === this.value) {
          return;
        }
      }
      this.inputEl.value = value;
    }
    this.value = value;
    this.isNotTyping = false;
    this.dispatch({ eventName: 'bl-change', detailObj: { value } }) as BlocksChangeEvent;
  };

  /**
   * 1) Check whether user pressing meta key such as command on mac, or alt+Enter and ctrl+Enter on Windows
   */
  private handleKeyDown(event: KeyboardEvent) {
    const hasModifier = event.metaKey || event.altKey || event.ctrlKey; /* 1 */

    if (event.key === KEYS.ENTER && !hasModifier) {
      this.formController.submit('submit');
    }

    if (this.type === 'email' && event.key === KEYS.SPACE) {
      event.preventDefault();
    }
  }

  /**
   * Prevent spaces from being pasted into the email type Input Field.
   */
  private handlePaste() {
    // setTimeout is used here to allow other native behaviors like maxlength validation
    setTimeout(() => {
      this.inputEl.value = this.inputEl.value.replace(/\s+/g, '');
      this.inputEl.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    });
  }

  /**
   * Convert value to desired format
   */
  private formatValue(value: string) {
    let formatter: Intl.NumberFormat;
    const options: Intl.NumberFormatOptions = {
      style: 'currency',
      currency: this.currency
    };

    formatter = new Intl.NumberFormat(this.locale, options);
    const newVal = formatter.format(Number(value));
    this.value = newVal;
  }

  /**
   * Convert formatted value string back to original value
   */
  private parseValue(value: string) {
    if (!value) return '';
    if (this._currency && this._group && this._decimal && this._numeral && this._index) {
      value = value
        .replace(this._currency, '')
        .trim()
        .replace(this._group, '')
        .replace(this._decimal, '.')
        .replace(this._numeral, this._index.toString());
    }

    return value ? (+value).toString() : '';
  }

  /**
   * To prevent input element value change on mouse scroll
   * 1) Make input element as readOnly
   * 2) On next tick, change readOnly as false
   */
  private preventWheelScroll() {
    this.hideStepper = true;
    if (!this.readonly && !this.disabled) {
      this.inputEl.readOnly = true; /*2*/
      // Make readonly as false immediately, on the next tick
      setTimeout(() => {
        this.inputEl.readOnly = false; /*3*/
      });
    }
  }

  /**
   * 1) We are gathering all ids which can be assinged to aria-describedby
   * 2) Combining aria-label and aria unit label if the input field has any unit in slots
   */
  private addAria() {
    let listOfIds = '';

    /* 1 */
    if (!this.hideWordCount && this.maxlength && this.maxlength > 0) {
      listOfIds = `${this.wordCountId}`;
    }
    if (this.hasFieldNote()) {
      this.ariaDescribedBy = this.ariaDescribedBy || nanoid();
      listOfIds = `${this.ariaDescribedBy} ${listOfIds}`;
    }
    if (this.hasCharInsertBeforeSlot) listOfIds = `${this.ariaDescribedBy} ${this.charBeforeId}`;
    if (this.hasCharInsertAfterSlot) listOfIds = `${listOfIds} ${this.charAfterId}`;
    if (this.hasAfterSlot) listOfIds = `${listOfIds} ${this.slotAfterId}`;

    const trimmedIds = listOfIds.trim();
    this.descriptionIds = trimmedIds.length > 0 ? trimmedIds : undefined;

    /* 2 */
    if (this.arialabel || this.ariaUnitLabel || this.hideLabel) {
      this.combinedAriaLabel = `${
        this.arialabel || (this.label && this.notation !== '' ? `${this.label || ''} ${this.notation || ''}` : this.label || '')
      } ${this.ariaUnitLabel ? this.ariaUnitLabel : ''}`;
    }
  }

  /**
   * parse input value and set isNotTyping for accessibility
   */
  private handleOnFocus(e: Event) {
    if (this.formatted) {
      this.value = this.parseValue((e.target as HTMLInputElement).value);
    }

    this.isNotTyping = e.type === 'focus';
  }

  /**
   * format input value and set isNotTyping to true for accessibility
   */
  private handleOnFocusOut(e: Event) {
    if (this.formatted) {
      this.formatValue((e.target as HTMLInputElement).value);
    }
    this.isNotTyping = true;
  }

  private renderLoading(componentClassNames: DirectiveResult<typeof ClassMapDirective>) {
    return html`
      <div part="base" class="${componentClassNames}">
        ${(this.size === 'md' || this.size === 'lg') && !this.hideLabel
          ? html`
              <cat-skeleton
                part="label-skeleton"
                exportparts=${this.getExportParts('skeleton-label', 'skeleton')}
                class="class3"
                size="sm"
                width="128px"
              ></cat-skeleton>
            `
          : nothing}
        <div part="body-loader" class="cat-c-input-field__input-loader">
          <cat-skeleton part="body-skeleton" exportparts=${this.getExportParts('skeleton-body', 'skeleton')}> </cat-skeleton>
        </div>

        ${this.hasFieldNote()
          ? html`
              <div part="field-note-loader" class="cat-c-input-field__fieldnote-loader">
                <cat-skeleton
                  part="field-note-skeleton"
                  exportparts=${this.getExportParts('skeleton-field-note', 'skeleton')}
                  size="sm"
                  width="80px"
                ></cat-skeleton>
              </div>
            `
          : nothing}
      </div>
    `;
  }

  private hasFieldNote() {
    return this.fieldNote || this.errorNote || this.warningNote || this.hasFieldNoteSlot || this.hasErrorSlot || this.hasWarningSlot;
  }

  private inputLabel() {
    return this.hideLabel
      ? nothing
      : html`<div part="label-wrapper" class="cat-c-input-field__label-wrapper">
          <label
            part="label-container"
            ?hidden="${this.type === 'hidden'}"
            class="cat-c-input-field__label"
            for="${ifDefined(this.fieldId)}"
            aria-hidden=${ifDefined(this.isNotTyping ? false : true)}
          >
            <span part="label-inner-wrapper" class="cat-c-input-field__label-inner-wrapper">
              <span part="part-label" class="cat-c-input-field--label"> ${this.label}</span>
              <span part="part-notation" class="cat-c-input-field__notation-text">${this.notation}</span>
            </span>
          </label>
          ${!this.hideWordCount && this.maxlength && this.maxlength > 0
            ? html`
                <slot part="part-word-count" name="word-count">
                  <span part="max-length" class="cat-c-input-field__maxlength" id=${ifDefined(this.wordCountId)}>
                    ${this.value?.length > 0 ? this.value.length : 0}/${this.maxlength}
                  </span>
                </slot>
              `
            : html`<slot part="part-custom-count" name="custom-count" class="cat-c-input-field__customCount"></slot>`}
        </div>`;
  }

  render(): TemplateResult<1> {
    this.addAria();

    const componentClassNames = this.componentClassNames('cat-c-input-field', {
      'cat-c-input-field--hidden-label': this.hideLabel,
      'cat-c-input-field--disabled': this.disabled,
      'cat-c-input-field--required': this.required,
      'cat-c-input-field--formatted': this.formatted,
      'cat-c-input-field--large': this.size === 'lg',
      'cat-c-input-field--medium': this.size === 'md',
      'cat-c-input-field--small': this.size === 'sm',
      'cat-is-error': this.status === 'error',
      'cat-is-warning': this.status === 'warning',
      'cat-is-readonly': this.readonly === true,
      'cat-is-active': this.isActive,
      'cat-is-hideStepper': this.hideStepper && this.type === 'number',
      'cat-has-icon': this.hasAfterSlot,
      'cat-has-icon--after': this.hasAfterSlot,
      'cat-has-char': this.hasCharInsertBeforeSlot || this.hasCharInsertAfterSlot,
      'cat-has-char--before': this.hasCharInsertBeforeSlot,
      'cat-has-char--after': this.hasCharInsertAfterSlot,
      'cat-has-placeholder': this.placeholder !== '',
      'cat-c-input-field--loader': this.isLoading === true,
      'cat-c-input-field--maxlength': !this.hideWordCount && this.maxlength && this.maxlength > 0 && !this.hideLabel,
      'cat-c-input-field--has-field-note': this.fieldNote || this.hasFieldNoteSlot,
      'cat-c-input-field--has-warning-note': this.warningNote || this.hasWarningSlot,
      'cat-c-input-field--has-error-note': this.errorNote || this.hasErrorSlot
    });

    if (this.isLoading) {
      return this.renderLoading(componentClassNames);
    } else if (this.type !== 'hidden') {
      return html`
        <div part="base" class="${componentClassNames}">
          <div part="wrapper" class="cat-c-input-field__wrap">
            <div part="body" class="cat-c-input-field__body">
              ${this.size === 'md' || this.size === 'lg' ? this.inputLabel() : nothing}
              <input
                part="input-field"
                class="cat-c-input-field__input"
                type="${this.type}"
                id="${ifDefined(this.fieldId)}"
                name="${ifDefined(this.name)}"
                .value="${live(this.value)}"
                @blur="${(e: Event) => this.handleOnFocusOut(e)}"
                @focus="${(e: Event) => this.handleOnFocus(e)}"
                ?readonly="${this.readonly}"
                @keydown=${this.handleKeyDown}
                @paste=${this.type === 'email' ? this.handlePaste : nothing}
                ?disabled="${this.disabled}"
                ?autofocus="${this.autoFocus}"
                autocomplete="${this.type === 'password' ? 'off' : this.autocomplete}"
                aria-describedby="${ifDefined(this.isNotTyping ? this.descriptionIds : undefined)}"
                aria-label="${ifDefined(this.combinedAriaLabel)}"
                aria-required="${ifDefined(!this.notation ? (this.required ? 'true' : undefined) : undefined)}"
                placeholder="${this.placeholder}"
                maxlength="${ifDefined(this.maxlength)}"
                minlength=${ifDefined(this.minlength)}
                min=${ifDefined(this.min)}
                max=${ifDefined(this.max)}
                pattern=${ifDefined(this.pattern)}
                step=${ifDefined(this.step)}
                @change=${(e: Event) => this.onChanged(e)}
                @input=${(e: Event) => this.onChanged(e)}
                @wheel=${this.disableWheelScroll ? this.preventWheelScroll : nothing}
              />
              ${this.status === 'error'
                ? html` <cat-icon-warning-diamond-filled
                    part="status-icon"
                    exportparts=${this.getExportParts('warning-diamond-filled', 'icon')}
                    color="error"
                    .iconTitle=${this.errorIconTitle}
                    class="cat-c-input-field__error-icon"
                  ></cat-icon-warning-diamond-filled>`
                : nothing}
              ${this.status === 'warning'
                ? html` <cat-icon-warning-triangle-filled
                    part="status-icon"
                    exportparts=${this.getExportParts('warning-triangle-filled', 'icon')}
                    color="knockout-brand"
                    .iconTitle=${this.warningIconTitle}
                    class="cat-c-input-field__warning-icon"
                  ></cat-icon-warning-triangle-filled>`
                : nothing}
              <slot part="part-after" name="after" class="cat-icon" id=${ifDefined(this.slotAfterId)}></slot>
              <slot
                part="part-char-insert-before"
                class="cat-c-input-field__char-insert cat-c-input-field__char-insert--before"
                name="char-insert-before"
                id=${ifDefined(this.charBeforeId)}
              ></slot>
              <slot
                part="part-char-insert-after"
                class="cat-c-input-field__char-insert cat-c-input-field__char-insert--after"
                name="char-insert-after"
                id=${ifDefined(this.charAfterId)}
              ></slot>
            </div>
          </div>

          <div part="footer" class=${this.hasFieldNote() ? 'cat-c-input-field__footer' : 'cat-u-is-vishidden'} aria-live=${this.arialive}>
            <div part="field-notes">
              <cat-field-note
                part="field-note-wrapper"
                exportparts=${this.getExportParts('field-note')}
                id=${ifDefined(this.fieldNote || this.hasFieldNoteSlot ? this.ariaDescribedBy : undefined)}
                .status=${this.status}
                ?isDisabled=${this.disabled}
                class="cat-c-input-field__field-note cat-c-input-field-fieldnote-slot"
              >
                <slot part="part-field-note" name="field-note"> ${this.fieldNote} </slot>
              </cat-field-note>

              <cat-field-note
                part="field-note-wrapper"
                exportparts=${this.getExportParts('field-note')}
                id=${ifDefined(this.warningNote || this.hasWarningSlot ? this.ariaDescribedBy : undefined)}
                ?isDisabled=${this.disabled}
                class="cat-c-input-field__field-note cat-c-input-field-warning-slot"
              >
                <slot part="part-warning" name="warning"> ${this.warningNote} </slot>
              </cat-field-note>

              <cat-field-note
                part="field-note-wrapper"
                exportparts=${this.getExportParts('field-note')}
                id=${ifDefined(this.errorNote || this.hasErrorSlot ? this.ariaDescribedBy : undefined)}
                .status=${'error'}
                ?isDisabled=${this.disabled}
                class="cat-c-input-field__field-note cat-c-input-field-error-slot"
              >
                <slot part="part-error" name="error"> ${this.errorNote} </slot>
              </cat-field-note>
            </div>
            <div class="cat-u-is-vishidden">
              ${this.charCountTemplate
                ? this.charCountTemplate
                : this.maxlength && this.value
                  ? `${this.maxlength - this.value?.length} of ${this.maxlength} characters remaining`
                  : html`${nothing}`}
            </div>
          </div>
        </div>
      `;
    } else {
      return html`${nothing}`;
    }
  }
}

if (customElements.get('cat-input-field') === undefined) {
  customElements.define('cat-input-field', BlocksInputField);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-input-field': BlocksInputField;
  }
}
